// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Khipu editor (the Khipu plan, 2026-10-08): makes a story or a
 * briefing step by step, or finishes one that was recorded. Opened from
 * **Your Khipu** after a recording, **Edit** in My Khipus, **Edit** in the
 * Khipu player, or **New Khipu** in Browse → Khipus.
 *
 * A panel at the top start corner (the lesson panel's frame, dragged by
 * its title bar): the Khipu's name, its steps (pick one; ↑ ↓ to reorder;
 * Delete, pressed twice), **Add a step from the globe**, and the step
 * picked: **Show on the globe**, **Use the globe now** (its dataset,
 * camera, date and overlay become what the globe shows), its title, its
 * words (captions, questions, tasks, notes for the presenter), its
 * pictures and links (https), when it moves on (when Next is pressed, or
 * after some seconds) and a quiz (a question with two to four answers, the
 * right one, a hint). **Preview** plays it from the step picked; the
 * player's **Edit** comes back here. **Save to my account** and
 * **Download (.zip)** check it first and say what stops them.
 *
 * Text is edited in the page's language; the other languages a Khipu has
 * are kept. Only the path and the name change (`khipuEdit`): the log, a
 * Studio shot and anything else pass through. Typing never redraws the
 * panel, so the cursor stays where it is.
 *
 * AYNI (owner, 2026-11-03: playlists retire for Khipus): **Start again
 * after the last step** (`x-ayni-loop`, `khipuCollect`), and the dataset
 * menu adds a dataset to the Khipu being edited, or takes out the steps it
 * added (the handle's `addDataset` / `removeDataset`).
 *
 * Written from scratch for AYNI: nothing here comes from TerraViz's code.
 */
import { getLocale, plural, t, type MessageKey } from '../i18n'
import { formatDate } from '../i18n/format'
import type { Khipu, KhipuEffects, KhipuMedia, KhipuPrompt, KhipuStep, KhipuText, KhipuTransition } from '../services/khipu'
import { localized } from '../services/khipu'
import {
  editProblems,
  finishEdit,
  insertStep,
  moveStep,
  removeStep,
  sceneToStep,
  setText,
  stepFromGlobe,
  updateStep,
} from '../services/khipuEdit'
import { saveKhipu } from '../services/khipuStore'
import { addDatasetStep, canRemoveDataset, hasDataset, khipuLoops, removeDatasetSteps, withLoop } from '../services/khipuCollect'
import { zipKhipu } from '../services/lessonService'
import type { SoloScene } from '../services/soloRecorder'
import { escapeAttr, escapeHtml } from './domUtils'
import { isPanelShown, makeFloating } from './floatingPanel'

export interface KhipuEditorHost {
  /** What the globe shows now. */
  scene(): SoloScene
  titleOf(datasetId: string): string | null
  layerTitleOf(layerId: string): string | null
  /** Put step `index` on the globe. */
  showStep(k: Khipu, index: number): void
  /** Play from step `index` in the Khipu player; its Edit comes back with the Khipu (`unsaved`: it has changes). */
  preview(k: Khipu, index: number, unsaved: boolean): void
  canKeep(): boolean
  exchangeOnly(): boolean
  announce(message: string): void
}

export interface KhipuEditorHandle {
  close(): void
  isOpen(): boolean
  /** The Khipu as edited so far. */
  khipu(): Khipu
  /** The step picked, from 0. */
  selected(): number
  /** Its name, in the page's language. */
  title(): string
  /** It has changes not saved or downloaded yet. */
  dirty(): boolean
  /** Whether some step shows this dataset. */
  hasDataset(datasetId: string): boolean
  /** Whether the dataset menu can take this dataset out (its plain steps, never the last step). */
  canRemoveDataset(datasetId: string): boolean
  /** The dataset as one more step at the end (false: it was already there). */
  addDataset(datasetId: string, title: string): boolean
  /** The dataset's plain steps taken out (false: none could be). */
  removeDataset(datasetId: string): boolean
}

type Kind = 'caption' | 'question' | 'task' | 'note'
const PROMPT_KINDS: Kind[] = ['caption', 'question', 'task', 'note']
const MEDIA_KINDS: KhipuMedia['kind'][] = ['link', 'image', 'video', 'panorama']
const LETTERS = ['A', 'B', 'C', 'D'] // i18n-exempt: option letters
const KIND_LABEL: Record<string, MessageKey> = {
  story: 'khipu.kind.story',
  briefing: 'khipu.kind.briefing',
}

let active: KhipuEditorHandle | null = null

/** The editor that is open, if any. */
export function activeKhipuEditor(): KhipuEditorHandle | null {
  return active
}

export function openKhipuEditor(
  start: Khipu,
  host: KhipuEditorHost,
  opts: {
    files?: Array<{ name: string; body: string }>
    startAt?: number
    returnFocus?: HTMLElement | null
    /** Not saved anywhere yet (a recording, or back from Preview with changes): ✕ asks first. */
    unsaved?: boolean
    /**
     * AYNI (Situation room, CR1): one more place to save to, first among the
     * buttons (the room's board: "Save as the board"); it says how it went.
     */
    saveAs?: { label: string; save: (k: Khipu) => Promise<{ ok: boolean; text: string }> }
  } = {},
): KhipuEditorHandle {
  active?.close()
  const lang = getLocale()
  let k: Khipu = start
  let sel = Math.min(Math.max(0, opts.startAt ?? 0), k.path.steps.length - 1)
  let dirty = opts.unsaved === true
  let armed: 'close' | 'delete' | null = null
  let armTimer: ReturnType<typeof setTimeout> | null = null
  let status: { text: string; ok: boolean } | null = null
  let folded = false

  const panel = document.createElement('section')
  panel.id = 'khipu-editor'
  panel.className = 'lesson-player khipu-player khipu-editor'
  panel.setAttribute('aria-label', t('edit.aria'))
  document.body.appendChild(panel)

  const text = (v: KhipuText | undefined) => localized(v, lang)
  const step = (): KhipuStep => k.path.steps[sel]
  const titles = { titleOf: host.titleOf, layerTitleOf: host.layerTitleOf }

  function stepLabel(s: KhipuStep, i: number): string {
    const own = text(s.title)
    if (own) return own
    const ref = typeof s.dataset === 'string' ? k.datasets.find((d) => d.key === s.dataset) : null
    return ref ? text(ref.title as KhipuText | undefined) || t('edit.step', { n: i + 1 }) : s.dataset === null ? t('edit.earth') : t('edit.step', { n: i + 1 })
  }

  function sceneLine(s: KhipuStep): string {
    const ref = typeof s.dataset === 'string' ? k.datasets.find((d) => d.key === s.dataset) : null
    const parts = [
      ref ? text(ref.title as KhipuText | undefined) : s.dataset === null ? t('edit.earth') : t('edit.datasetOpen'),
      s.view ? `${s.view.lat.toFixed(1)}°, ${s.view.lon.toFixed(1)}° · ×${s.view.zoom.toFixed(1)}` : s.region ? t('edit.region') : '',
      s.time?.at ? formatDate(new Date(s.time.at), { dateStyle: 'medium' }) + (s.time.playing ? ` · ${t('edit.playing')}` : '') : s.time?.relative ? t(s.time.relative === 'latest' ? 'edit.latest' : 'edit.first') : '',
      (s.layers ?? []).length ? t('edit.overlay') : '',
      (s.autorotate as { on?: boolean } | undefined)?.on ? t('edit.spinning') : '',
      // Khipu 1.2: the satellites, with the selected object's number and whether the camera follows it.
      s.satellites?.on ? [t('edit.satellites'), s.satellites.selected ? `NORAD ${s.satellites.selected}` : '', s.satellites.follow ? t('edit.satFollowing') : ''].filter(Boolean).join(' ') : '',
      Array.isArray(s.pins) && s.pins.length ? plural(s.pins.length, { one: 'edit.pins.one', other: 'edit.pins.other' }) : '',
    ]
    return parts.filter(Boolean).join(' · ')
  }

  function promptsHtml(s: KhipuStep): string {
    const rows = (s.prompts ?? []).map((p, j) => {
      const kind = (PROMPT_KINDS as string[]).includes(p.kind) ? p.kind : 'caption'
      return `<li class="ke-row">
        <select class="ke-prompt-kind" data-j="${j}" aria-label="${escapeAttr(t('edit.words.kind'))}">${PROMPT_KINDS.map((x) => `<option value="${x}"${x === kind ? ' selected' : ''}>${escapeHtml(t(`edit.words.${x}` as MessageKey))}</option>`).join('')}</select>
        <textarea class="ke-prompt-text" data-j="${j}" rows="2" aria-label="${escapeAttr(t('edit.words.text'))}">${escapeHtml(text(p.text))}</textarea>
        <button type="button" class="lesson-icon ke-prompt-remove" data-j="${j}" aria-label="${escapeAttr(t('edit.remove'))}">&#x2715;</button>
      </li>`
    }).join('')
    return `<ul class="ke-list" role="list">${rows}</ul><button type="button" class="lesson-link ke-prompt-add">${escapeHtml(t('edit.words.add'))}</button>`
  }

  function mediaHtml(s: KhipuStep): string {
    const rows = (s.media ?? []).map((m, j) => `<li class="ke-row ke-media">
        <select class="ke-media-kind" data-j="${j}" aria-label="${escapeAttr(t('edit.media.kind'))}">${MEDIA_KINDS.map((x) => `<option value="${x}"${x === m.kind ? ' selected' : ''}>${escapeHtml(t(`edit.media.${x}` as MessageKey))}</option>`).join('')}</select>
        <input type="url" class="ke-media-url" data-j="${j}" value="${escapeAttr(m.url)}" placeholder="https://" aria-label="${escapeAttr(t('edit.media.url'))}">
        <input type="text" class="ke-media-title" data-j="${j}" value="${escapeAttr(text(m.title))}" placeholder="${escapeAttr(t('edit.media.title'))}" aria-label="${escapeAttr(t('edit.media.title'))}">
        <button type="button" class="lesson-icon ke-media-remove" data-j="${j}" aria-label="${escapeAttr(t('edit.remove'))}">&#x2715;</button>
      </li>`).join('')
    return `<ul class="ke-list" role="list">${rows}</ul><button type="button" class="lesson-link ke-media-add">${escapeHtml(t('edit.media.add'))}</button>`
  }

  function quizHtml(s: KhipuStep): string {
    const q = s.quiz as { question?: KhipuText; options?: KhipuText[]; correctIndex?: number; hint?: KhipuText } | undefined
    const on = !!q
    let body = ''
    if (q) {
      const options = q.options ?? []
      body = `<label class="ke-label">${escapeHtml(t('edit.quiz.question'))}<input type="text" class="ke-quiz-question" value="${escapeAttr(text(q.question))}"></label>
        <fieldset class="ke-quiz-options"><legend>${escapeHtml(t('edit.quiz.answers'))}</legend>
        ${options.map((o, j) => `<div class="ke-row">
          <input type="radio" name="ke-quiz-right" class="ke-quiz-right" data-j="${j}"${q.correctIndex === j ? ' checked' : ''} aria-label="${escapeAttr(t('edit.quiz.right', { letter: LETTERS[j] }))}">
          <span class="khipu-quiz-letter" aria-hidden="true">${LETTERS[j]}</span>
          <input type="text" class="ke-quiz-option" data-j="${j}" value="${escapeAttr(text(o))}" aria-label="${escapeAttr(t('edit.quiz.answer', { letter: LETTERS[j] }))}">
          ${options.length > 2 ? `<button type="button" class="lesson-icon ke-quiz-remove" data-j="${j}" aria-label="${escapeAttr(t('edit.remove'))}">&#x2715;</button>` : ''}
        </div>`).join('')}
        ${options.length < 4 ? `<button type="button" class="lesson-link ke-quiz-add">${escapeHtml(t('edit.quiz.add'))}</button>` : ''}
        </fieldset>
        <label class="ke-label">${escapeHtml(t('edit.quiz.hint'))}<input type="text" class="ke-quiz-hint" value="${escapeAttr(text(q.hint))}"></label>`
    }
    return `<label class="ke-check"><input type="checkbox" class="ke-quiz-on"${on ? ' checked' : ''}> ${escapeHtml(t('edit.quiz.on'))}</label>${body}`
  }

  /** Khipu 1.1: how the step is reached, what the camera does while it is shown, and its effects (the player and the Studio play them). */
  function motionHtml(s: KhipuStep): string {
    const tr = s.transition ?? {}
    const fx = s.effects ?? {}
    const first = sel === 0
    const opt = (v: string, label: string, on: boolean) => `<option value="${v}"${on ? ' selected' : ''}>${escapeHtml(label)}</option>`
    const callouts = (fx.callouts ?? []).map((c, j) => `<li class="ke-row">
        <input type="text" class="ke-fx-callout" data-j="${j}" value="${escapeAttr(c.label)}" maxlength="80" aria-label="${escapeAttr(t('studio.fx.calloutLabel'))}">
        <button type="button" class="lesson-icon ke-fx-callout-remove" data-j="${j}" aria-label="${escapeAttr(t('edit.remove'))}">&#x2715;</button>
      </li>`).join('')
    return `${first ? '' : `<label class="ke-label">${escapeHtml(t('studio.stop.arrive'))}
        <select class="ke-move">${opt('', t('edit.motion.asBefore'), !s.transition)}${(['fly', 'glide', 'cut'] as const).map((m) => opt(m, t(`studio.move.${m}`), tr.move === m)).join('')}</select></label>
      ${s.transition ? `<label class="ke-label">${escapeHtml(t('studio.move.seconds'))}<input type="number" class="ke-move-seconds" min="0" max="30" step="0.5" value="${escapeAttr(String(tr.seconds ?? ''))}"></label>` : ''}`}
      <label class="ke-label">${escapeHtml(t('studio.during'))}
        <select class="ke-during">${(['still', 'orbit', 'pushIn', 'pullOut'] as const).map((d) => opt(d, t(`studio.during.${d}`), (tr.during ?? 'still') === d)).join('')}</select></label>
      <label class="ke-label">${escapeHtml(t('edit.motion.hold'))}<input type="number" class="ke-hold" min="0.5" max="600" step="0.5" value="${escapeAttr(String(s.hold ?? ''))}" placeholder="4"></label>
      <label class="ke-check"><input type="checkbox" class="ke-fx-spot"${fx.spotlight ? ' checked' : ''}> ${escapeHtml(t('studio.fx.spotlight'))}</label>
      ${fx.spotlight ? `<label class="ke-label">${escapeHtml(t('studio.fx.radius'))}<input type="number" class="ke-fx-radius" min="0.5" max="90" step="0.5" value="${escapeAttr(String(fx.spotlight.radius))}"></label>` : ''}
      <label class="ke-check"><input type="checkbox" class="ke-fx-vignette"${fx.vignette ? ' checked' : ''}> ${escapeHtml(t('studio.fx.vignette'))}</label>
      <label class="ke-label">${escapeHtml(t('studio.fx.card'))}<input type="text" class="ke-fx-card" maxlength="120" value="${escapeAttr(text(fx.titleCard?.text))}"></label>
      <ul class="ke-list" role="list">${callouts}</ul>
      ${(fx.callouts?.length ?? 0) < 6 ? `<button type="button" class="lesson-link ke-fx-callout-add">${escapeHtml(t('studio.fx.calloutAdd'))}</button>` : ''}
      <p class="lesson-notice">${escapeHtml(t('edit.motion.note'))}</p>`
  }

  /** Change the step's effects; an empty set is removed. */
  function withEffects(patch: Partial<KhipuEffects>): void {
    const next: KhipuEffects = { ...(step().effects ?? {}) }
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) delete (next as Record<string, unknown>)[key]
      else (next as Record<string, unknown>)[key] = value
    }
    change(updateStep(k, sel, { effects: Object.keys(next).length ? next : undefined }))
  }

  /** Change the step's transition; an empty one is removed. */
  function withTransition(patch: Partial<KhipuTransition>): void {
    const next: KhipuTransition = { ...(step().transition ?? {}) }
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) delete (next as Record<string, unknown>)[key]
      else (next as Record<string, unknown>)[key] = value
    }
    change(updateStep(k, sel, { transition: Object.keys(next).length ? next : undefined }))
  }

  /** The centre of the view, as a step keeps places. */
  function centre(): { lat: number; lon: number } | null {
    const v = host.scene().view
    return v ? { lat: Math.round(v.lat * 1e4) / 1e4, lon: Math.round(v.lon * 1e4) / 1e4 } : null
  }

  function formHtml(): string {
    const s = step()
    const timed = s.advance?.mode === 'timer'
    return `<fieldset class="ke-form">
      <legend>${escapeHtml(t('edit.stepOf', { n: sel + 1, total: k.path.steps.length }))}</legend>
      <p class="ke-scene">${escapeHtml(sceneLine(s))}</p>
      <div class="lesson-links">
        <button type="button" class="lesson-link ke-show">${escapeHtml(t('edit.show'))}</button>
        <button type="button" class="lesson-link ke-use-globe">${escapeHtml(t('edit.useGlobe'))}</button>
      </div>
      <label class="ke-check"><input type="checkbox" class="ke-spin"${(s.autorotate as { on?: boolean } | undefined)?.on ? ' checked' : ''}> ${escapeHtml(t('edit.spin'))}</label>
      ${s.satellites?.on && s.satellites.selected ? `<label class="ke-check"><input type="checkbox" class="ke-sat-follow"${s.satellites.follow ? ' checked' : ''}> ${escapeHtml(t('edit.satFollow'))}</label>` : ''}
      <label class="ke-label">${escapeHtml(t('edit.title'))}<input type="text" class="ke-step-title" value="${escapeAttr(text(s.title))}" maxlength="200"></label>
      <h4 class="ke-h">${escapeHtml(t('edit.words'))}</h4>
      ${promptsHtml(s)}
      <h4 class="ke-h">${escapeHtml(t('edit.media'))}</h4>
      ${mediaHtml(s)}
      <h4 class="ke-h">${escapeHtml(t('edit.motion'))}</h4>
      ${motionHtml(s)}
      <h4 class="ke-h">${escapeHtml(t('edit.advance'))}</h4>
      <div class="ke-row">
        <select class="ke-advance" aria-label="${escapeAttr(t('edit.advance'))}">
          <option value="manual"${timed ? '' : ' selected'}>${escapeHtml(t('edit.advance.manual'))}</option>
          <option value="timer"${timed ? ' selected' : ''}>${escapeHtml(t('edit.advance.timer'))}</option>
        </select>
        ${timed ? `<input type="number" class="ke-seconds" min="1" max="600" step="1" value="${escapeAttr(String(s.advance?.seconds ?? 10))}" aria-label="${escapeAttr(t('edit.advance.seconds'))}"> <span>${escapeHtml(t('edit.advance.unit'))}</span>` : ''}
      </div>
      <h4 class="ke-h">${escapeHtml(t('edit.quiz'))}</h4>
      ${quizHtml(s)}
    </fieldset>`
  }

  function render(): void {
    const problems = editProblems(k)
    const kind = KIND_LABEL[k.kind] ? t(KIND_LABEL[k.kind]) : ''
    panel.classList.toggle('folded', folded)
    panel.innerHTML = `
      <div class="lesson-head">
        <button type="button" class="lesson-icon lesson-grip" aria-label="${escapeAttr(t('khipu.player.move'))}" title="${escapeAttr(t('khipu.player.move'))}">&#x283F;</button>
        <div class="lesson-head-text">
          <span class="lesson-title">${escapeHtml(t('edit.heading'))}</span>
          <span class="lesson-progress">${escapeHtml([kind, k.path.steps.length === 1 ? t('khipu.record.steps.one') : t('khipu.record.steps.other', { n: k.path.steps.length })].filter(Boolean).join(' · '))}</span>
        </div>
        <button type="button" class="lesson-icon lesson-fold" aria-expanded="${String(!folded)}" aria-label="${escapeAttr(t(folded ? 'khipu.player.expand' : 'khipu.player.collapse'))}">${folded ? '&#x25BE;' : '&#x25B4;'}</button>
        <button type="button" class="lesson-icon lesson-close${armed === 'close' ? ' armed' : ''}" aria-label="${escapeAttr(t(armed === 'close' ? 'edit.closeConfirm' : 'edit.close'))}" title="${escapeAttr(t(armed === 'close' ? 'edit.closeConfirm' : 'edit.close'))}">&#x2715;</button>
      </div>
      <div class="lesson-body"${folded ? ' hidden' : ''}>
        ${armed === 'close' ? `<p class="lesson-save-status lesson-save-error" role="alert">${escapeHtml(t('edit.closeConfirm'))}</p>` : ''}
        <label class="ke-label">${escapeHtml(t('khipu.record.name'))}<input type="text" class="ke-name" value="${escapeAttr(text(k.title))}" maxlength="120"></label>
        <label class="ke-check"><input type="checkbox" class="ke-loop"${khipuLoops(k) ? ' checked' : ''}> ${escapeHtml(t('edit.loop'))}</label>
        <h3 class="ke-h">${escapeHtml(t('edit.steps'))}</h3>
        <ol class="ke-steps">${k.path.steps.map((s, i) => `<li><button type="button" class="ke-step${i === sel ? ' current' : ''}" data-i="${i}"${i === sel ? ' aria-current="step"' : ''}>${escapeHtml(`${i + 1}. ${stepLabel(s, i)}`)}</button></li>`).join('')}</ol>
        <div class="ke-step-tools">
          <button type="button" class="lesson-btn ke-add">${escapeHtml(t('edit.add'))}</button>
          <button type="button" class="lesson-icon ke-up" aria-label="${escapeAttr(t('edit.up'))}" title="${escapeAttr(t('edit.up'))}"${sel === 0 ? ' disabled' : ''}>&#x2191;</button>
          <button type="button" class="lesson-icon ke-down" aria-label="${escapeAttr(t('edit.down'))}" title="${escapeAttr(t('edit.down'))}"${sel >= k.path.steps.length - 1 ? ' disabled' : ''}>&#x2193;</button>
          <button type="button" class="lesson-btn lesson-btn-quiet ke-delete${armed === 'delete' ? ' armed' : ''}"${k.path.steps.length <= 1 ? ' disabled' : ''}>${escapeHtml(t(armed === 'delete' ? 'edit.deleteConfirm' : 'edit.delete'))}</button>
        </div>
        ${formHtml()}
        ${problems.length ? `<ul class="ke-problems" aria-label="${escapeAttr(t('edit.problems'))}">${problems.map((p) => `<li>${escapeHtml(p.step >= 0 ? `${t('edit.step', { n: p.step + 1 })}: ${t(p.key)}` : t(p.key))}</li>`).join('')}</ul>` : ''}
        <div class="lesson-nav khipu-nav">
          <button type="button" class="lesson-btn lesson-btn-quiet ke-preview">${escapeHtml(t('edit.preview'))}</button>
          ${opts.saveAs ? `<button type="button" class="lesson-btn ke-save-as">${escapeHtml(opts.saveAs.label)}</button>` : ''}
          ${host.canKeep() ? `<button type="button" class="lesson-btn${opts.saveAs ? ' lesson-btn-quiet' : ''} ke-save">${escapeHtml(t('lessons.player.save'))}</button>` : ''}
          <button type="button" class="lesson-btn${host.canKeep() ? ' lesson-btn-quiet' : ''} ke-download">${escapeHtml(t('khipu.player.download'))}</button>
        </div>
        ${status ? `<p class="lesson-save-status${status.ok ? '' : ' lesson-save-error'}" role="status">${escapeHtml(status.text)}</p>` : ''}
      </div>`
  }

  /** Redraw, keeping the focus on the same control where it still exists. */
  function redraw(focus?: string): void {
    const was = focus ?? focusKey(document.activeElement)
    render()
    floating.keepOnScreen()
    if (was) panel.querySelector<HTMLElement>(was)?.focus()
  }

  function focusKey(el: Element | null): string | undefined {
    if (!(el instanceof HTMLElement) || !panel.contains(el)) return undefined
    const cls = [...el.classList].find((c) => c.startsWith('ke-') || c.startsWith('lesson-'))
    if (!cls) return undefined
    const j = el.dataset.j ?? el.dataset.i
    return `.${cls}${j !== undefined ? `[data-${el.dataset.j !== undefined ? 'j' : 'i'}="${j}"]` : ''}`
  }

  function change(next: Khipu): void {
    k = next
    dirty = true
    status = null
  }

  function arm(what: 'close' | 'delete'): void {
    armed = what
    if (armTimer) clearTimeout(armTimer)
    armTimer = setTimeout(() => {
      armed = null
      redraw()
    }, 4000)
  }

  /** The Khipu as it would be saved, or null with the reasons shown. */
  function ready(): Khipu | null {
    const problems = editProblems(k)
    if (problems.length) {
      status = { text: t('edit.cannotSave'), ok: false }
      const first = problems.find((p) => p.step >= 0)
      if (first) sel = first.step
      redraw()
      host.announce(`${t('edit.cannotSave')} ${problems.map((p) => t(p.key)).join(' ')}`)
      return null
    }
    return finishEdit(k, lang, Date.now())
  }

  async function save(trigger: HTMLElement): Promise<void> {
    const done = ready()
    if (!done) return
    trigger.setAttribute('aria-busy', 'true')
    status = { text: t('lessons.player.saving'), ok: true }
    redraw()
    const result = await saveKhipu(done, opts.files ?? [])
    if (result.ok) {
      k = done
      dirty = false
    }
    status = result.ok
      ? { text: t('lessons.player.saved', { date: formatDate(new Date(result.value.keptUntil), { dateStyle: 'medium' }) }), ok: true }
      : { text: t(result.error === 'too_many' ? 'lessons.player.saveTooMany' : result.error === 'full' ? 'lessons.player.saveFull' : result.error === 'not_configured' ? 'lessons.player.saveUnavailable' : 'lessons.player.saveFailed'), ok: false }
    redraw()
    host.announce(status.text)
  }

  async function saveAs(trigger: HTMLElement): Promise<void> {
    const done = ready()
    if (!done || !opts.saveAs) return
    trigger.setAttribute('aria-busy', 'true')
    status = { text: t('lessons.player.saving'), ok: true }
    redraw()
    const result = await opts.saveAs.save(done)
    if (result.ok) {
      k = done
      dirty = false
    }
    status = result
    redraw()
    host.announce(status.text)
  }

  async function download(): Promise<void> {
    const done = ready()
    if (!done) return
    const { blob, filename } = await zipKhipu(done, opts.files ?? [], { exchange: host.exchangeOnly() })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
    dirty = false
    status = { text: t('lessons.player.downloaded', { filename }), ok: true }
    redraw()
    host.announce(status.text)
  }

  // Lists in a step: a copy with item j changed (or removed, with null).
  function withPrompt(j: number, next: KhipuPrompt | null): void {
    const list = [...(step().prompts ?? [])]
    if (next) list[j] = next
    else list.splice(j, 1)
    change(updateStep(k, sel, { prompts: list.length ? list : undefined }))
  }
  function withMedia(j: number, next: KhipuMedia | null): void {
    const list = [...(step().media ?? [])]
    if (next) list[j] = next
    else list.splice(j, 1)
    change(updateStep(k, sel, { media: list.length ? list : undefined }))
  }
  type Quiz = { question: KhipuText; options: KhipuText[]; correctIndex: number; hint?: KhipuText; difficulty: string }
  const quiz = (): Quiz => step().quiz as Quiz
  function withQuiz(next: Partial<Quiz> | null): void {
    change(updateStep(k, sel, { quiz: next ? { ...quiz(), ...next } : undefined }))
  }

  const floating = makeFloating(panel, { headSelector: '.lesson-head', gripClass: 'lesson-grip' })

  panel.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button')
    if (!b || b.disabled) return
    const j = Number(b.dataset.j)
    const c = b.classList
    if (c.contains('lesson-close')) {
      if (dirty && armed !== 'close') {
        arm('close')
        redraw('.lesson-close')
        return
      }
      return handle.close()
    }
    if (c.contains('lesson-fold')) {
      folded = !folded
      return redraw('.lesson-fold')
    }
    if (c.contains('ke-step')) {
      sel = Number(b.dataset.i)
      armed = null
      return redraw(`.ke-step[data-i="${sel}"]`)
    }
    if (c.contains('ke-add')) {
      const r = stepFromGlobe(k, host.scene(), titles)
      change(insertStep(r.khipu, r.step, sel))
      sel += 1
      redraw('.ke-step-title')
      return host.announce(t('edit.added', { n: sel + 1 }))
    }
    if (c.contains('ke-up') || c.contains('ke-down')) {
      const delta = c.contains('ke-up') ? -1 : 1
      change(moveStep(k, sel, delta))
      sel += delta
      redraw(c.contains('ke-up') && sel > 0 ? '.ke-up' : sel < k.path.steps.length - 1 ? '.ke-down' : '.ke-up')
      return host.announce(t('edit.moved', { n: sel + 1 }))
    }
    if (c.contains('ke-delete')) {
      if (armed !== 'delete') {
        arm('delete')
        return redraw('.ke-delete')
      }
      armed = null
      change(removeStep(k, sel))
      sel = Math.min(sel, k.path.steps.length - 1)
      redraw(`.ke-step[data-i="${sel}"]`)
      return host.announce(t('edit.deleted'))
    }
    if (c.contains('ke-show')) return host.showStep(k, sel)
    if (c.contains('ke-use-globe')) {
      change(sceneToStep(k, sel, host.scene(), titles))
      redraw('.ke-use-globe')
      return host.announce(t('edit.usedGlobe'))
    }
    if (c.contains('ke-prompt-add')) {
      change(updateStep(k, sel, { prompts: [...(step().prompts ?? []), { kind: 'caption', audience: 'everyone', text: {} }] }))
      return redraw(`.ke-prompt-text[data-j="${(step().prompts ?? []).length - 1}"]`)
    }
    if (c.contains('ke-prompt-remove')) {
      withPrompt(j, null)
      return redraw('.ke-prompt-add')
    }
    if (c.contains('ke-media-add')) {
      change(updateStep(k, sel, { media: [...(step().media ?? []), { kind: 'link', url: '' }] }))
      return redraw(`.ke-media-url[data-j="${(step().media ?? []).length - 1}"]`)
    }
    if (c.contains('ke-media-remove')) {
      withMedia(j, null)
      return redraw('.ke-media-add')
    }
    if (c.contains('ke-fx-callout-add')) {
      const at = centre()
      if (!at) return
      withEffects({ callouts: [...(step().effects?.callouts ?? []), { ...at, label: '', value: true }] })
      return redraw(`.ke-fx-callout[data-j="${(step().effects?.callouts ?? []).length - 1}"]`)
    }
    if (c.contains('ke-fx-callout-remove')) {
      withEffects({ callouts: (step().effects?.callouts ?? []).filter((_, i) => i !== j) })
      const left = step().effects?.callouts ?? []
      if (!left.length) withEffects({ callouts: undefined })
      return redraw('.ke-fx-callout-add')
    }
    if (c.contains('ke-quiz-add')) {
      withQuiz({ options: [...quiz().options, {}] })
      return redraw(`.ke-quiz-option[data-j="${quiz().options.length - 1}"]`)
    }
    if (c.contains('ke-quiz-remove')) {
      const options = quiz().options.filter((_, i) => i !== j)
      const right = quiz().correctIndex
      withQuiz({ options, correctIndex: right === j ? 0 : right > j ? right - 1 : right })
      return redraw('.ke-quiz-question')
    }
    if (c.contains('ke-preview')) return host.preview(finishEdit(k, lang, Date.now()), sel, dirty)
    if (c.contains('ke-save-as')) return void saveAs(b)
    if (c.contains('ke-save')) return void save(b)
    if (c.contains('ke-download')) return void download()
  })

  // Typing changes the Khipu without a redraw, so the cursor stays put.
  panel.addEventListener('input', (e) => {
    const el = e.target as HTMLInputElement
    const j = Number(el.dataset.j)
    const c = el.classList
    if (c.contains('ke-name')) change({ ...k, title: setText(k.title, lang, el.value) ?? {} })
    else if (c.contains('ke-step-title')) change(updateStep(k, sel, { title: setText(step().title, lang, el.value) }))
    else if (c.contains('ke-prompt-text')) withPrompt(j, { ...step().prompts![j], text: setText(step().prompts![j].text, lang, el.value) ?? {} })
    else if (c.contains('ke-media-url')) withMedia(j, { ...step().media![j], url: el.value.trim() })
    else if (c.contains('ke-media-title')) {
      const title = setText(step().media![j].title, lang, el.value)
      const { title: _old, ...rest } = step().media![j]
      withMedia(j, title ? { ...rest, title } : rest)
    } else if (c.contains('ke-seconds')) change(updateStep(k, sel, { advance: { mode: 'timer', seconds: Number(el.value) } }))
    else if (c.contains('ke-quiz-question')) withQuiz({ question: setText(quiz().question, lang, el.value) ?? {} })
    else if (c.contains('ke-quiz-option')) withQuiz({ options: quiz().options.map((o, i) => (i === j ? setText(o, lang, el.value) ?? {} : o)) })
    else if (c.contains('ke-quiz-hint')) withQuiz({ hint: setText(quiz().hint, lang, el.value) })
    else if (c.contains('ke-move-seconds')) withTransition({ seconds: el.value === '' ? undefined : Math.min(30, Math.max(0, Number(el.value) || 0)) })
    else if (c.contains('ke-hold')) change(updateStep(k, sel, { hold: el.value === '' ? undefined : Math.min(600, Math.max(0.5, Number(el.value) || 4)) }))
    else if (c.contains('ke-fx-radius') && step().effects?.spotlight) withEffects({ spotlight: { ...step().effects!.spotlight!, radius: Math.min(90, Math.max(0.5, Number(el.value) || 8)) } })
    else if (c.contains('ke-fx-card')) {
      const words = setText(step().effects?.titleCard?.text, lang, el.value)
      withEffects({ titleCard: words && Object.values(words).some((v) => v.trim()) ? { text: words } : undefined })
    } else if (c.contains('ke-fx-callout')) withEffects({ callouts: (step().effects?.callouts ?? []).map((co, i) => (i === j ? { ...co, label: el.value } : co)) })
    else return
    // The step list shows the step's title as it is typed.
    if (c.contains('ke-step-title')) {
      const item = panel.querySelector(`.ke-step[data-i="${sel}"]`)
      if (item) item.textContent = `${sel + 1}. ${stepLabel(step(), sel)}`
    }
  })

  // Choices change the form's shape: redraw.
  panel.addEventListener('change', (e) => {
    const el = e.target as HTMLInputElement
    const j = Number(el.dataset.j)
    const c = el.classList
    if (c.contains('ke-prompt-kind')) {
      const kind = el.value as Kind
      withPrompt(j, { ...step().prompts![j], kind, audience: kind === 'note' ? 'presenter' : 'everyone' })
    } else if (c.contains('ke-media-kind')) withMedia(j, { ...step().media![j], kind: el.value as KhipuMedia['kind'] })
    else if (c.contains('ke-advance')) change(updateStep(k, sel, { advance: el.value === 'timer' ? { mode: 'timer', seconds: 10 } : { mode: 'manual' } }))
    else if (c.contains('ke-quiz-on')) change(updateStep(k, sel, { quiz: el.checked ? { question: {}, options: [{}, {}], correctIndex: 0, difficulty: 'easy' } : undefined }))
    else if (c.contains('ke-quiz-right')) withQuiz({ correctIndex: j })
    else if (c.contains('ke-move')) withTransition({ move: el.value ? el.value as KhipuTransition['move'] : undefined, ...(el.value ? {} : { seconds: undefined }) })
    else if (c.contains('ke-during')) withTransition({ during: el.value === 'still' ? undefined : el.value as KhipuTransition['during'] })
    else if (c.contains('ke-fx-spot')) {
      const at = centre()
      withEffects({ spotlight: el.checked && at ? { ...at, radius: 8, dim: 0.55 } : undefined })
    } else if (c.contains('ke-fx-vignette')) withEffects({ vignette: el.checked ? 0.5 : undefined })
    else if (c.contains('ke-loop')) change(withLoop(k, el.checked))
    else if (c.contains('ke-sat-follow')) {
      const sat = step().satellites
      if (sat?.on) change(updateStep(k, sel, { satellites: { ...sat, follow: el.checked } }))
    }
    else if (c.contains('ke-spin')) {
      const was = step().autorotate as { on?: boolean; speed?: number } | undefined
      change(updateStep(k, sel, { autorotate: el.checked ? { on: true, speed: was?.speed ?? 1 } : { on: false } }))
    }
    else return
    redraw(c.contains('ke-quiz-on') && el.checked ? '.ke-quiz-question'
      : c.contains('ke-advance') && el.value === 'timer' ? '.ke-seconds'
        : c.contains('ke-move') ? '.ke-move'
          : c.contains('ke-during') ? '.ke-during'
            : c.contains('ke-fx-spot') ? '.ke-fx-spot'
              : c.contains('ke-fx-vignette') ? '.ke-fx-vignette' : undefined)
  })

  const handle: KhipuEditorHandle = {
    close: () => {
      if (armTimer) clearTimeout(armTimer)
      floating.leave()
      if (active === handle) active = null
      const back = opts.returnFocus
      if (back?.isConnected) back.focus()
    },
    isOpen: () => isPanelShown(panel),
    khipu: () => k,
    selected: () => sel,
    title: () => text(k.title),
    dirty: () => dirty,
    hasDataset: (id) => hasDataset(k, id),
    canRemoveDataset: (id) => canRemoveDataset(k, id),
    addDataset: (id, title) => {
      if (hasDataset(k, id)) return false
      change(addDatasetStep(k, id, title))
      sel = k.path.steps.length - 1
      redraw()
      return true
    },
    removeDataset: (id) => {
      const next = removeDatasetSteps(k, id)
      if (!next) return false
      change(next)
      sel = Math.min(sel, k.path.steps.length - 1)
      redraw()
      return true
    },
  }
  active = handle
  render()
  panel.querySelector<HTMLElement>('.ke-name')?.focus()
  return handle
}
